import React, { useContext, useState } from 'react';
import styles from './Wishlist.module.scss';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { CustomSpinner } from '../../shared/Spinner';
import { Balance } from '../../components/Balance';
import { useTranslation } from 'react-i18next';
import { Player } from '../../components/UI/Player';
import { AuthContext } from '../../context/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { PinConfirmationModal } from '../../components/UI/Modals/PinConfirmationModal';
import { OrderModal } from '../../components/UI/Modals/OrderModal';
import ReactPaginate from 'react-paginate';
import { useBookmarksQuery } from './query';
import { WishlistBookmarksModel } from './query/types';
import { useBookmarksDeleteMutation } from './mutation';
import { URLWishlistQueryStateModel } from './types';
import {generateImageUrl} from "../../utils/cdnService";
import StreamButton from '../../components/MovieDetailButtons/StreamButton/StreamButton';
import DownloadHDButton from '../../components/MovieDetailButtons/DownloadHDButton/DownloadHDButton';
import BmButton from '../../components/MovieDetailButtons/BmButton/BmButton';

const Wishlist = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const { isAuthenticated, loading, user, updateUser } = authContext;
  const [playerStatus, setPlayerStatus] = useState<boolean>(false);
  const [orderVisibility, setOrderVisibility] = useState<boolean>(false);
  const [selectedButton, setSelectedButton] = useState<string>('');
  const [selectedPrice, setSelectedPrice] = useState<number>(0);
  const [show, setShow] = useState<boolean>(false);
  const [selectedMovie, setSelectedMovie] = useState<WishlistBookmarksModel>();
  const [cfMediaID, setCfMediaID] = useState<string>('');
  const [query, setQuery] = useState<URLWishlistQueryStateModel>({
    page: 1,
  });
  const navigate = useNavigate();

  const { bookmarksData, bookmarksMeta, isBookmarksQueryLoading, refetchBookmarksQuery } = useBookmarksQuery(query);
  const { mutate } = useBookmarksDeleteMutation(query);

  const handleDelete = async (id: number) => {
    mutate(id);
  };


  const renderMenu = (movie: WishlistBookmarksModel) => {
    if (!isBookmarksQueryLoading && movie && isAuthenticated && !loading) {
      return (
        <Col xs={12}>
          <Row>
            {movie &&
              movie.stream &&
              <StreamButton movie={movie}
                            setSelectedButton={setSelectedButton}
                            setSelectedMovie={setSelectedMovie}
                            setOrderVisibility={setOrderVisibility}
                            setSelectedPrice={setSelectedPrice}
                            setShow={setShow}/> }
            {movie &&
              movie.stream &&
              <DownloadHDButton movie={movie}
                                setShow={setShow}
                                setSelectedButton={setSelectedButton}
                                setSelectedMovie={setSelectedMovie}
                                setOrderVisibility={setOrderVisibility}
                                setSelectedPrice={setSelectedPrice}/>}
            {movie &&
              movie.stream &&
              <BmButton movie={movie}/>}
          </Row>
        </Col>
      );
    }
  };
  const handleClose = () => setShow(false);

  const renderStream = () => {
    if (!isBookmarksQueryLoading && bookmarksData && isAuthenticated && playerStatus) {
      return (
        <Player banner={selectedMovie?.banner} onHide={onHide} id={selectedMovie?.id} controls muted src={cfMediaID} />
      );
    }
  };
  const onHide = () => {
    refetchBookmarksQuery()
    setPlayerStatus(false);
  };

  const renderVideos = () => {
    if (isBookmarksQueryLoading) {
      return <CustomSpinner />;
    } else if (bookmarksData && bookmarksData?.length === 0) {
      return <h1>No data</h1>;
    } else {
      return (
        bookmarksData &&
        bookmarksData?.length > 0 &&
        bookmarksData.map((video) => {
          return (
            <Col sm="12"  className={styles.movieWrapper} key={video.id}>
              <Row className={styles.movie}>
                <Col sm="3">
                  <figure className={styles.movieFigure}>
                    <img className="img-responsive" src={generateImageUrl('gcore', 0, 0, video.image)} alt="bookmark" />
                  </figure>
                </Col>
                <Col sm="9">
                  <Row>
                    <Col lg="7" md="6" sm="12" xs="12">
                      <h2 onClick={() => navigate(`/${language}/movie/${video.id}`)} className={styles.headline}>
                        {video.title}
                      </h2>
                    </Col>
                    <Col lg="3" md="3" sm="6" xs="12">
                      <div className={styles.headerButtons}>
                        <button onClick={() => handleDelete(video.id)}>
                          <i className="fa fa-trash" aria-hidden="true"></i>
                          {t('wishListPage.removeMovie')}
                        </button>
                      </div>
                    </Col>
                    <Col lg="2" md="3" sm="6" xs={false}>
                      <div className={styles.headerButtons}>
                        <button onClick={() => navigate(`/${language}/movie/${video.id}`)}>
                          <i className="fa fa-film" aria-hidden="true"></i>
                          {t('wishListPage.goToMovie')}
                        </button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs="12">
                      <div className={styles.info}>
                        <span className={styles.infoLabel}>{t('wishListPage.label')}</span>
                        <span className={styles.infoValue}> {video.label}</span>
                      </div>
                      <p className="mt-15">{video.description}</p>
                    </Col>
                    {renderMenu(video)}
                  </Row>
                </Col>
              </Row>
            </Col>
          );
        })
      );
    }
  };
  const renderPagination = () => {
    if (bookmarksMeta && !isBookmarksQueryLoading) {
      return (
        <ReactPaginate
          previousLabel={<i className="fa fa-chevron-left" ></i>}
          nextLabel={<i className="fa fa-chevron-right" ></i>}
          breakLabel={'...'}
          breakClassName={styles.breakMe}
          pageCount={bookmarksMeta && bookmarksMeta.last_page}
          marginPagesDisplayed={1}
          pageRangeDisplayed={3}
          onPageChange={handlePageChange}
          forcePage={(query.page as number) - 1}
          containerClassName={'pagination'}
          activeClassName={styles.active}
          previousClassName={query.page === 1 ? `${styles.hideButton}` : `${styles.showButton}`}
          nextClassName={
            bookmarksMeta && query.page === bookmarksMeta.last_page ? `${styles.hideButton}` : `${styles.showButton}`
          }
        />
      );
    }
    return null;
  };

  const handlePageChange = (data: { selected: number }) => {
    const selectedPage = data.selected + 1;
    setQuery({
      ...query,
      page: selectedPage,
    });
  };

  const handleSuccessPinConfirmationModal = (purchaseData:any) => {
    if (purchaseData && selectedButton === 'play') {
      setCfMediaID(purchaseData.data.url);
      setPlayerStatus(true);
      setShow(false);
      setOrderVisibility(false);
    } else {
      setShow(false);
      setOrderVisibility(false);
    }
  }

  const handleSuccessOrderModal = (purchaseData:any) => {
    if (purchaseData && selectedButton === 'play') {
      setCfMediaID(purchaseData.data.url);
      setPlayerStatus(true);
    }
  }

  return (
    <Container>
      <div className={styles.pageHeader}>
        <Balance />
      </div>
      <h2>{t('wishListPage.title')}</h2>
      <Row>
        <Col sm={{ span: 9, offset: 3 }}>
          <div className={styles.paginationWrapper}>
            <div className={styles.pagination}>{renderPagination()}</div>
          </div>
        </Col>
      </Row>

      <Row>{renderVideos()}</Row>

      <Row>
        <Col sm={{ span: 9, offset: 3 }}>
          <div className={styles.paginationWrapper}>
            <div className={styles.pagination}>{renderPagination()}</div>
          </div>
        </Col>
      </Row>

      <Row>
        <PinConfirmationModal
          key={`pin-modal-${show}`}
          selectedButton={selectedButton}
          handleSuccess={(purchaseData) => {
            handleSuccessPinConfirmationModal(purchaseData);
          }}
          centered={true}
          show={show}
          onHide={handleClose}
          movieId={selectedMovie?.id?.toString() || ''}
          movieTitle={selectedMovie?.title || ''}
        />
        <OrderModal
          key={`order-modal-${orderVisibility}`}
          selectedButton={selectedButton}
          balance={user && (user.balance?.toFixed(2) as string)}
          location={location.pathname}
          price={selectedPrice}
          handleSuccess={(purchaseData) => {
            handleSuccessOrderModal(purchaseData)
          }}
          onRefetch={refetchBookmarksQuery}
          centered={true}
          show={orderVisibility}
          onHide={() => setOrderVisibility(false)}
          movieId={selectedMovie?.id?.toString() || ''}
          movieTitle={selectedMovie?.title || ''}
        />
      </Row>

      {bookmarksData && cfMediaID && renderStream()}
    </Container>
  );
};

export default Wishlist;
